import { Person, VehicleTruck, VehicleTrailer, PersonManager } from 'phx-core';

import { KEBAB_MENU_LABEL_TYPES } from '@app/common-components/kebab-menu.component';
import { FORM_ELEMENT_KEYS } from '@app/misc/mobile-view-builder/templates/form-elements';

import { INTL_PHONE_FORMAT } from '@app/services/custom-validators';

import { IOverriddenSettings, IStrings } from '@app/admin/fleet-card/settings/settings.interface';

export const FLEET_CARDS_URL_PREFIX: string = 'fleet-cards';

export enum REQUEST_CASH_ADVANCE_STATUS {
  Hidden = 'hidden',
  RequestFeature = 'request_cash_advance_feature',
  UnderReview = 'cash_advance_feature_under_review',
  Declined = 'declined',
  Approved = 'approved',
}

export type IEnumRequestCashAdvanceValues =
  REQUEST_CASH_ADVANCE_STATUS.Hidden |
  REQUEST_CASH_ADVANCE_STATUS.RequestFeature |
  REQUEST_CASH_ADVANCE_STATUS.UnderReview |
  REQUEST_CASH_ADVANCE_STATUS.Declined |
  REQUEST_CASH_ADVANCE_STATUS.Approved;

export type IEnumRequestCashAdvance = typeof REQUEST_CASH_ADVANCE_STATUS;

export enum TABS {
  Billing = 'overview',
  CardManagement = 'card-management',
  Settings = 'settings',
  Configuration = 'configuration',
  Orders = 'orders',
}

export const FLEET_CARDS_NO_SHADOW_URLS: string[] = [
  `admin/${FLEET_CARDS_URL_PREFIX}/${TABS.CardManagement}`,
  `admin/${FLEET_CARDS_URL_PREFIX}/${TABS.Orders}`,
];

export const FLEET_CARDS_ORDERS_TAB_URL: string = `admin/${FLEET_CARDS_URL_PREFIX}/${TABS.Orders}`;

export const FLEET_CARDS_ORDERS_CHECKOUT_URL: string = `admin/${FLEET_CARDS_URL_PREFIX}/${TABS.Orders}/new`;

export enum CARD_APPLICATION_STATUS {
  draft = 'draft',
  needs_verification = 'needs_verification',
  submitted = 'submitted',
  credit_declined = 'credit_declined',
  kyb_pending = 'kyb_pending',
  credit_started = 'credit_started',
  issued = 'cards_issued',
  approved = 'kyb_approved',
}

export enum CARD_STATUS {
  unactivated = 'unactivated',
  assigned = 'assigned',
  active = 'active',
  active_locked = 'active_locked',
  active_unlocked = 'active_unlocked',
  suspended = 'suspended',
  unassigned = 'unassigned',
  terminated = 'terminated',
}

export enum CARD_STATUS_TYPES {
  unactivated = 'neutral',
  assigned = 'positive',
  active = 'positive',
  active_locked = 'positive',
  active_unlocked = 'positive',
  suspended = 'caution',
  unassigned = 'accent',
  terminated = 'neutral',
}

export const DEACTIVATION_REASONS = [
  {
    label: $localize`The card was lost/stolen`,
    value: '00',
  },
  {
    label: $localize`The card is damaged`,
    value: '01',
  },
  {
    label: $localize`Cardholder is no longer a part of the company`,
    value: '02',
  },
  {
    label: $localize`Other`,
    value: '99',
  },
];

export const DEACTIVATION_REASONS_V2 = [
  {
    label: $localize`The card was lost`,
    value: '10',
  },
  {
    label: $localize`The card was stolen`,
    value: '23',
  },
  {
    label: $localize`Cardholder is no longer a part of the company`,
    value: '15',
  },
  {
    label: $localize`The card is damaged / Other`,
    value: '31',
  },
];

export enum PAYMENT_STATUS {
  FUTURE_SCHEDULED = 'future_scheduled',
  SCHEDULED = 'scheduled',
  INITIATED = 'initiated',
  APPROVED = 'approved',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  RETURNED = 'returned',
  FAILED = 'failed',
  REVERSED = 'reversed',
  DENIED = 'denied',
  CANCELLED = 'cancelled',
}

export enum SPEND_CONTROLS_FILTER_KEYS {
  WITH_SPEND_CONTROLS = 'with_spend_controls',
  WITHOUT_SPEND_CONTROLS = 'without_spend_controls',
}

export enum FRAUD_CONTROLS_FILTER_KEYS {
  WITH_FRAUD_CONTROLS = 'with_fraud_controls',
  WITHOUT_FRAUD_CONTROLS = 'without_fraud_controls',
}

export const FILTER_GENERAL_STATUS = {
  ENABLED: 'enabled',
  DISABLED: 'disabled',
};

// TODO:
// Auto decline - remove PROXIMITY_BASED_DECLINE after permanent overrides is GAed
// and keep AUTO_DECLINE_FUEL_TRANSACTION
export const FILTER_KEYS = {
  MOBILE_BASED_UNLOCK: 'mobile_based_unlock',
  FLAG_FUEL_TRANSACTION: 'flag_fuel_txn',
  PROXIMITY_BASED_DECLINE: 'proximity_based_decline',
  AUTO_DECLINE_FUEL_TRANSACTION: 'auto_decline_fuel_txn',
};


// TODO:
// Auto decline - remove PROXIMITY_BASED_DECLINE_ENABLED/DISABLED
// after permanent overrides is GAed
// and keep AUTO_DECLINE_FUEL_TRANSACTION_ENABLED/DISABLED
export enum CARDS_SECURITY_SETTINGS_FILTER_KEYS {
  WITH_SECURITY_SETTINGS = 'with_security_settings',
  WITHOUT_SECURITY_SETTINGS = 'without_security_settings',
  MOBILE_BASED_UNLOCK_ENABLED = 'MOBILE_BASED_UNLOCK_ENABLED',
  MOBILE_BASED_UNLOCK_DISABLED = 'MOBILE_BASED_UNLOCK_DISABLED',
  PROXIMITY_BASED_DECLINE_ENABLED = 'PROXIMITY_BASED_DECLINE_ENABLED',
  PROXIMITY_BASED_DECLINE_DISABLED = 'PROXIMITY_BASED_DECLINE_DISABLED',
  AUTO_DECLINE_FUEL_TRANSACTION_ENABLED = 'AUTO_DECLINE_FUEL_TRANSACTION_ENABLED',
  AUTO_DECLINE_FUEL_TRANSACTION_DISABLED = 'AUTO_DECLINE_FUEL_TRANSACTION_DISABLED',
  FLAG_FUEL_TRANSACTION_ENABLED = 'FLAG_FUEL_TRANSACTION_ENABLED',
  FLAG_FUEL_TRANSACTION_DISABLED = 'FLAG_FUEL_TRANSACTION_DISABLED',
}

export enum TRANSACTION_METADATA_FILTER_KEYS {
  ENABLED = 'transaction_requirements_enabled',
  DISABLED = 'transaction_requirements_disabled',
}

export const I18N_CARD_STATUS = {
  [CARD_STATUS.unactivated]: {
    label: $localize`Unactivated`,
  },
  [CARD_STATUS.assigned]: {
    label: $localize`Inactive`,
  },
  [CARD_STATUS.active]: {
    label: $localize`Active`,
  },
  [CARD_STATUS.active_locked]: {
    label: $localize`Locked`,
  },
  [CARD_STATUS.active_unlocked]: {
    label: $localize`Active`,
  },
  [CARD_STATUS.unassigned]: {
    label: $localize`Unassigned`,
  },
  [CARD_STATUS.suspended]: {
    label: $localize`Frozen`,
  },
  [CARD_STATUS.terminated]: {
    label: $localize`Deactivated`,
  },
};

/* mapping
 * BE to FE payment status mapping
 * 'Future Scheduled' = Scheduled
 */
export const I18N_PAYMENT_STATUS = {
  [PAYMENT_STATUS.FUTURE_SCHEDULED]: {
    label: $localize`Scheduled`,
  },
};

/* Newer mapping of payment status using ledger_status
 * BE to FE payment status mapping
 * 'Initiated' = Initiated
 * 'Processing' = Processing
 * 'Completed' = Complete
 * 'Returned' = Failed
 * 'Failed' = Failed
 * 'Reversed' = Reversed
 */
export const I18N_PAYMENT_LEDGER_STATUS = {
  [PAYMENT_STATUS.INITIATED]: {
    label: $localize`Initiated`,
  },
  [PAYMENT_STATUS.PROCESSING]: {
    label: $localize`Processing`,
  },
  [PAYMENT_STATUS.COMPLETED]: {
    label: $localize`Complete`,
  },
  [PAYMENT_STATUS.RETURNED]: {
    label: $localize`Failed`,
  },
  [PAYMENT_STATUS.FAILED]: {
    label: $localize`Failed`,
  },
  [PAYMENT_STATUS.REVERSED]: {
    label: $localize`Reversed`,
  },
};

export const CARD_STATUS_DROPDOWN_V2 = [
  {
    label: $localize`Active`,
    key: CARD_STATUS.active,
  },
  {
    label: $localize`Frozen`,
    key: CARD_STATUS.suspended,
  },
  {
    label: $localize`Deactivated`,
    key: CARD_STATUS.terminated,
  },
];

export const CARD_STATUS_DROPDOWN_V3 = [
  {
    label: $localize`Active`,
    key: CARD_STATUS.active,
  },
  {
    label: $localize`Locked`,
    key: CARD_STATUS.active_locked,
  },
  {
    label: $localize`Frozen`,
    key: CARD_STATUS.suspended,
  },
  {
    label: $localize`Deactivated`,
    key: CARD_STATUS.terminated,
  },
];

export const CARD_STATUS_DROPDOWN_PHX_OLD = [
  {
    label: $localize`Active`,
    value: CARD_STATUS.active,
  },
  {
    label: $localize`Frozen`,
    value: CARD_STATUS.suspended,
  },
  {
    label: $localize`Deactivated`,
    value: CARD_STATUS.terminated,
  },
];

export const CARD_STATUS_DROPDOWN_PHX = [
  {
    label: $localize`Active`,
    value: CARD_STATUS.active,
  },
  {
    label: $localize`Locked`,
    value: CARD_STATUS.active_locked,
  },
  {
    label: $localize`Frozen`,
    value: CARD_STATUS.suspended,
  },
  {
    label: $localize`Deactivated`,
    value: CARD_STATUS.terminated,
  },
];

export const CARD_STATUS_DROPDOWN_PHX_V2 = [
  {
    label: $localize`Active`,
    value: CARD_STATUS.active,
  },
  {
    label: $localize`Locked`,
    value: CARD_STATUS.active_locked,
  },
  {
    label: $localize`Frozen`,
    value: CARD_STATUS.suspended,
  },
  {
    label: $localize`Unassigned`,
    value: CARD_STATUS.unassigned,
  },
  {
    label: $localize`Deactivated`,
    value: CARD_STATUS.terminated,
  },
];

export const SPEND_CONTROLS_DROPDOWN = [
  {
    label: $localize`Cards with spend controls`,
    key: SPEND_CONTROLS_FILTER_KEYS.WITH_SPEND_CONTROLS,
  },
  {
    label: $localize`Cards without spend controls`,
    key: SPEND_CONTROLS_FILTER_KEYS.WITHOUT_SPEND_CONTROLS,
  },
];

export const FRAUD_CONTROLS_DROPDOWN = [
  {
    label: $localize`Cards with fraud controls`,
    key: FRAUD_CONTROLS_FILTER_KEYS.WITH_FRAUD_CONTROLS,
  },
  {
    label: $localize`Cards without fraud controls`,
    key: FRAUD_CONTROLS_FILTER_KEYS.WITHOUT_FRAUD_CONTROLS,
  },
];

export const FRAUD_CONTROLS_DROPDOWNV2 = [
  {
    label: $localize`Security Settings Enabled`,
    key: FRAUD_CONTROLS_FILTER_KEYS.WITH_FRAUD_CONTROLS,
  },
  {
    label: $localize`Security Settings Disabled`,
    key: FRAUD_CONTROLS_FILTER_KEYS.WITHOUT_FRAUD_CONTROLS,
  },
];

export const FRAUD_CONTROLS_DROPDOWN_V3 = [
  {
    label: $localize`Security Settings Enabled`,
    key: CARDS_SECURITY_SETTINGS_FILTER_KEYS.WITH_SECURITY_SETTINGS,
  },
  {
    label: $localize`Security Settings Disabled`,
    key: CARDS_SECURITY_SETTINGS_FILTER_KEYS.WITHOUT_SECURITY_SETTINGS,
  },
];

export const FRAUD_CONTROLS_DROPDOWN_PHX = [
  {
    label: $localize`Cards with fraud controls`,
    value: FRAUD_CONTROLS_FILTER_KEYS.WITH_FRAUD_CONTROLS,
  },
  {
    label: $localize`Cards without fraud controls`,
    value: FRAUD_CONTROLS_FILTER_KEYS.WITHOUT_FRAUD_CONTROLS,
  },
];

export const FRAUD_CONTROLS_DROPDOWN_PHX_V2 = [
  {
    label: $localize`Security Settings Enabled`,
    value: FRAUD_CONTROLS_FILTER_KEYS.WITH_FRAUD_CONTROLS,
  },
  {
    label: $localize`Security Settings Disabled`,
    value: FRAUD_CONTROLS_FILTER_KEYS.WITHOUT_FRAUD_CONTROLS,
  },
];

export const FRAUD_CONTROLS_DROPDOWN_PHX_V3 = [
  {
    label: $localize`Security Settings Enabled`,
    value: CARDS_SECURITY_SETTINGS_FILTER_KEYS.WITH_SECURITY_SETTINGS,
  },
  {
    label: $localize`Security Settings Disabled`,
    value: CARDS_SECURITY_SETTINGS_FILTER_KEYS.WITHOUT_SECURITY_SETTINGS,
  },
];

const FRAUD_CONTROLS_DROPDOWN_GROUPS = {
  mobileBasedUnlock: $localize`Mobile-Based Unlock`,
  flagFuelTransactions: $localize`Flag fuel transactions`,
  autoDeclineTransactions: $localize`Auto-decline transactions`,
};

export const FRAUD_CONTROLS_DROPDOWN_PHX_V4 = [
  {
    groupLabel: FRAUD_CONTROLS_DROPDOWN_GROUPS.mobileBasedUnlock,
  },
  {
    label: $localize`Enabled`,
    value: CARDS_SECURITY_SETTINGS_FILTER_KEYS.MOBILE_BASED_UNLOCK_ENABLED,
  },
  {
    label: $localize`Disabled`,
    value: CARDS_SECURITY_SETTINGS_FILTER_KEYS.MOBILE_BASED_UNLOCK_DISABLED,
  },
];

export const FRAUD_CONTROLS_DROPDOWN_PHX_V4_WITH_PROXIMITY_BASED_DECLINE = [
  {
    groupLabel: FRAUD_CONTROLS_DROPDOWN_GROUPS.mobileBasedUnlock,
  },
  {
    label: $localize`Enabled`,
    value: CARDS_SECURITY_SETTINGS_FILTER_KEYS.MOBILE_BASED_UNLOCK_ENABLED,
  },
  {
    label: $localize`Disabled`,
    value: CARDS_SECURITY_SETTINGS_FILTER_KEYS.MOBILE_BASED_UNLOCK_DISABLED,
  },
  {
    groupLabel: FRAUD_CONTROLS_DROPDOWN_GROUPS.autoDeclineTransactions,
  },
  {
    label: $localize`Enabled`,
    value: CARDS_SECURITY_SETTINGS_FILTER_KEYS.PROXIMITY_BASED_DECLINE_ENABLED,
  },
  {
    label: $localize`Disabled`,
    value: CARDS_SECURITY_SETTINGS_FILTER_KEYS.PROXIMITY_BASED_DECLINE_DISABLED,
  },
];

export const FRAUD_CONTROLS_DROPDOWN_PHX_V4_WITH_FUEL_BASED_DECLINE = [
  {
    groupLabel: FRAUD_CONTROLS_DROPDOWN_GROUPS.mobileBasedUnlock,
  },
  {
    label: $localize`Enabled`,
    value: CARDS_SECURITY_SETTINGS_FILTER_KEYS.MOBILE_BASED_UNLOCK_ENABLED,
  },
  {
    label: $localize`Disabled`,
    value: CARDS_SECURITY_SETTINGS_FILTER_KEYS.MOBILE_BASED_UNLOCK_DISABLED,
  },
  {
    groupLabel: FRAUD_CONTROLS_DROPDOWN_GROUPS.flagFuelTransactions,
  },
  {
    label: $localize`Enabled`,
    value: CARDS_SECURITY_SETTINGS_FILTER_KEYS.FLAG_FUEL_TRANSACTION_ENABLED,
  },
  {
    label: $localize`Disabled`,
    value: CARDS_SECURITY_SETTINGS_FILTER_KEYS.FLAG_FUEL_TRANSACTION_DISABLED,
  },
  {
    groupLabel: FRAUD_CONTROLS_DROPDOWN_GROUPS.autoDeclineTransactions,
  },
  {
    label: $localize`Enabled`,
    value: CARDS_SECURITY_SETTINGS_FILTER_KEYS.AUTO_DECLINE_FUEL_TRANSACTION_ENABLED,
  },
  {
    label: $localize`Disabled`,
    value: CARDS_SECURITY_SETTINGS_FILTER_KEYS.AUTO_DECLINE_FUEL_TRANSACTION_DISABLED,
  },
];

export const TRANSACTION_METADATA_DROPDOWN_OPTIONS = [
  {
    label: $localize`Txn. Requirements Enabled`,
    value: TRANSACTION_METADATA_FILTER_KEYS.ENABLED,
  },
  {
    label: $localize`Txn. Requirements Disabled`,
    value: TRANSACTION_METADATA_FILTER_KEYS.DISABLED,
  },
];

export const CARD_STATUS_DROPDOWN = [
  {
    label: $localize`Active`,
    key: CARD_STATUS.active,
  },
  {
    label: $localize`Frozen`,
    key: CARD_STATUS.suspended,
  },
  {
    label: $localize`Deactivated`,
    key: CARD_STATUS.terminated,
  },
  {
    label: $localize`Unactivated`,
    key: CARD_STATUS.unactivated,
  },
  {
    label: $localize`All`,
    key: 'all',
  },
];

export const MESSAGES: IStrings = {
  ERROR: $localize`Something went wrong. Please try again.`,
  CARD_ASSIGN_ERROR: $localize`We are unable to activate this card based on the info you provided`,
  USER_HAS_EXISTING_CARD_ERROR: $localize`A card is already assigned to this driver. Deactivate their current card or choose a different driver.`,
  VEHICLE_HAS_EXISTING_CARD_ERROR: $localize`A card is already assigned to this vehicle. Deactivate the current card or choose a different vehicle.`,
  CARD_LAST_FOUR_DIGITS_INCORRECT: $localize`We don’t recognize this card. Please try again.`,
  CARD_DEACTIVATION_SUCCESS: $localize`Card deactivated successfully`,
  CARD_DEACTIVATION_ERROR: $localize`Sorry, something went wrong. Please try again later or contact Support at 1-855-434-3564.`,
  UPDATE_FRAUD_CONTROL_SUCCESS: $localize`Changes have been saved.`,
  UPDATE_FRAUD_CONTROL_ERROR: $localize`Something went wrong. Please try again.`,
  UPDATE_MERCHANT_RESTRICTION_SUCCESS: $localize`Changes have been saved.`,
  UPDATE_MERCHANT_RESTRICTION_FAILURE: $localize`Something went wrong. Please try again.`,
  NOT_EMPTY: $localize`This info is required.`,
  INVALID: $localize`Please provide a valid phone number.`,
  CARD_ALREADY_ASSIGNED: $localize`This card is already assigned. Choose a different card and try again.`,
  CARD_INVALID_STATUS: $localize`This card is already activated. Choose a different card and try again.`,
  // tslint:disable-next-line: max-line-length
  CUSTOM_FLEET_USER_CARD_TO_VEHICLE_ERROR: $localize`This card cannot be assigned to a fleet user. Choose a different card and try again.`,
  CUSTOM_DRIVER_CARD_TO_VEHICLE_ERROR: $localize`This card cannot be assigned to a vehicle. Choose a different card and try again.`,
  // tslint:disable-next-line: max-line-length
  CUSTOM_VEHICLE_CARD_TO_DRIVER_ERROR: $localize`This card cannot be assigned to a driver. Choose a different card and try again.`,
  RESET_OVERRIDE_CHANGES: $localize`Changes have been reset.`,
  CARD_UNASSIGNMENT_SUCCESS: $localize`Card unassigned`,
  OVERRIDES_CLEARED: $localize`Overrides cleared`,
  CARDS_OVERRIDES_CLEARED: $localize`Overrides have been cleared`,
  FUEL_LEVEL_CANNOT_BE_ZERO: $localize`Fuel level cannot be zero`,
  INHERIT_CARD_SECURITY_SETTINGS_ONLY: $localize`This card will inherit the "Card security" settings from your organization.`,
  INHERIT_FUEL_THEFT_SECURITY_SETTINGS_ONLY: $localize`This card will inherit the "Card security" settings from your organization.`,
  INHERIT_BOTH_CARD_AND_FUEL_THEFT_SECURITY_SETTINGS: $localize`This card will inherit the "Card security" settings from your organization.`,
  DOT_ADDED_SUCCESSFULLY: $localize`DOT number added`,
  DOT_REMOVED_SUCCESSFULLY: $localize`DOT number removed`,
};

export const CARD_HELPLINE = '+1 855-434-3564';

export const WAITLIST_STATUSES = {
  NOT_JOINED: 'not_joined',
  JOINED: 'joined',
};

export enum PAYMENT_TYPE {
  AUTO_PAY = 'auto_pay',
  MANUAL_PAY = 'manual_pay',
  RISK_OPS = 'risk_ops',
  RISK_OPS_SCHEDULED = 'risk_ops_scheduled',
}

export const I18N_PAYMENT_TYPE = {
  [PAYMENT_TYPE.AUTO_PAY]: {
    label: $localize`Auto-Pay`,
  },
  [PAYMENT_TYPE.MANUAL_PAY]: {
    label: $localize`Manual`,
  },
  [PAYMENT_TYPE.RISK_OPS]: {
    label: $localize`Motive Rep, Manual`,
  },
  [PAYMENT_TYPE.RISK_OPS_SCHEDULED]: {
    label: $localize`Scheduled`,
  },
};

export const FRAUD_CONTROL_FORM_KEYS = {
  textAuthStatus: 'textAuthStatus',
  phoneNumber: 'phoneNumber',
};

export const FRAUD_CONTROL_FORM_KEYSV2 = {
  status: 'status',
  phoneNumber: 'phoneNumber',
};

export enum TOOLTIP_TRIGGER {
  HOVER = 'hover',
  NONE = 'none',
}

export const FRAUD_CONTROL_FORM_KEYSV3 = {
  status: 'status',
  phoneNumber: 'phoneNumber',
  vehicleProximityBasedDeclineSettings: 'vehicleProximityBasedDeclineSettings',

  fuelLevelLimit: 'fuelLevelLimit',
  fuelLevelExceedsLimitEnabled: 'fuelLevelExceedsLimitEnabled',
  fuelTypeMismatchEnabled: 'fuelTypeMismatchEnabled',
  fuelTankLevelMismatchEnabled: 'fuelTankLevelMismatchEnabled',
  vehicleLocationMismatchEnabled: 'vehicleLocationMismatchEnabled',
};

// Maps the names of the security settings to the names used in the server request
// these should match with IServerUpdateSecurityControlRequest
export const securitySettingsNamesMapToServerRequest = {
  status: 'mobile_based_unlock',
  fuelLevelLimit: 'fuel_level_limit',
  fuelTypeMismatchEnabled: 'fuel_type_mismatch_enabled',
  vehicleLocationMismatchEnabled: 'vehicle_location_mismatch_enabled',
  fuelLevelExceedsLimitEnabled: 'fuel_level_exceeds_limit_enabled',
  fuelTankLevelMismatchEnabled: 'fuel_tank_level_mismatch_enabled',
  vehicleProximityBasedDeclineSettings: 'vehicle_proximity_based_decline_settings',
};

export const FRAUD_CONTROL_SECURITY_SETTINGS = {
  driverCardSecurity: 'driverCardSecurity',
  vehicleCardSecurity: 'vehicleCardSecurity',
  fleetUserCardSecurity: 'fleetUserCardSecurity',
  fuelTypeMismatchSettings: 'fuelTypeMismatchSettings',
  mobileBasedUnlockSettings: 'mobileBasedUnlockSettings',
  fuelLevelExceedsLimitSettings: 'fuelLevelExceedsLimitSettings',
  fuelTankLevelMismatchSettings: 'fuelTankLevelMismatchSettings',
  vehicleLocationMismatchSettings: 'vehicleLocationMismatchSettings',
  vehicleProximityBasedDeclineSettings: 'vehicleProximityBasedDeclineSettings',
};

export const FRAUD_CONTROLV3_FORM_CONFIG = {
  [FRAUD_CONTROL_FORM_KEYSV2.status]: {
    label: $localize`Mobile-Based Unlock`,
    type: FORM_ELEMENT_KEYS.SINGLE_SELECT,
    required: true,
    options: [
      { key: 'active', label: $localize`Enabled` },
      { key: 'inactive', label: $localize`Disabled` },
    ],
  },
  [FRAUD_CONTROL_FORM_KEYSV2.phoneNumber]: {
    label: $localize`Phone number`,
    placeholder: '___-___-____',
    pattern: INTL_PHONE_FORMAT,
  },
};

export const FRAUD_CONTROLV4_FORM_CONFIG = {
  [FRAUD_CONTROL_FORM_KEYSV3.status]: {
    label: $localize`Mobile-Based Unlock`,
    type: FORM_ELEMENT_KEYS.TOGGLE,
    required: false,
  },
  [FRAUD_CONTROL_FORM_KEYSV3.phoneNumber]: {
    label: $localize`Phone number`,
    placeholder: '___-___-____',
    pattern: INTL_PHONE_FORMAT,
  },
  [FRAUD_CONTROL_FORM_KEYSV3.vehicleProximityBasedDeclineSettings]: {
    label: $localize`Auto-decline transactions`,
    type: FORM_ELEMENT_KEYS.TOGGLE,
    required: false,
  },
  [FRAUD_CONTROL_FORM_KEYSV3.fuelLevelExceedsLimitEnabled]: {
    label: $localize`Fuel level is above`,
    type: FORM_ELEMENT_KEYS.TOGGLE,
    required: false,
  },
  [FRAUD_CONTROL_FORM_KEYSV3.fuelLevelLimit]: {
    type: FORM_ELEMENT_KEYS.NUMBER,
    required: false,
  },
  [FRAUD_CONTROL_FORM_KEYSV3.fuelTankLevelMismatchEnabled]: {
    label: $localize`Fuel tank level mismatch`,
    type: FORM_ELEMENT_KEYS.TOGGLE,
    required: false,
  },
  [FRAUD_CONTROL_FORM_KEYSV3.fuelTypeMismatchEnabled]: {
    label: $localize`Fuel type mismatch`,
    type: FORM_ELEMENT_KEYS.TOGGLE,
    required: false,
  },
  [FRAUD_CONTROL_FORM_KEYSV3.vehicleLocationMismatchEnabled]: {
    label: $localize`Vehicle location mismatch`,
    type: FORM_ELEMENT_KEYS.TOGGLE,
    required: false,
  },
};

export const TXN_REQUIREMENTS_FORM_CONFIG = {
  [FRAUD_CONTROL_FORM_KEYSV2.status]: {
    label: $localize`Transaction Requirements`,
    type: FORM_ELEMENT_KEYS.SINGLE_SELECT,
    required: true,
    options: [
      { key: 'active', label: $localize`Enabled` },
      { key: 'inactive', label: $localize`Disabled` },
    ],
  },
  [FRAUD_CONTROL_FORM_KEYSV3.phoneNumber]: {
    label: $localize`Phone number`,
    placeholder: '___-___-____',
    pattern: INTL_PHONE_FORMAT,
  },
};

export const FRAUD_CONTROL_TEXT = $localize`SMS card unlock is enabled.`;

export const FRAUD_CONTROL_TEXTV2 = $localize`Mobile-Based Unlock is enabled.`;

export const NO_FRAUD_CONTROL_TEXT = $localize`Fraud controls are disabled.`;

export const NO_FRAUD_CONTROL_TEXTV2 = $localize`Mobile-Based Unlock is disabled.`;

export const NO_PHONE_NUMBER_TEXT = $localize`to enable SMS Card Unlock for this cardholder.`;

export const NO_PHONE_NUMBER_TEXTV2 = $localize`to enable Mobile-Based Unlock for this cardholder.`;

export const NO_PHONE_NUMBER_TEXTV3 = $localize`to enable Mobile-Based Unlock for this card.`;

export const UN_LOCKED_CARD_TEXT = $localize`Card is not available for use.`;

export const DEACTIVATED_CARD_TEXT = $localize`Card can no longer be used to make purchases.`;

export const ACTIVE_CARD_TEXT = $localize`Card is available for use.`;

export const FROZEN_CARD_TEXT = $localize`Card is temporarily disabled.`;

export const UNASSIGNED_CARD_TEXT = $localize`Card not available to use until assigned.`;

export const PHONE_NUMBER_TEXT_UNLOCK_PRODUCTION = '+1 408-645-7882';

export enum PLAID_USAGE {
  CHANGE_BANK_ACCOUNT = 'change_bank_account',
  CARD_APPLICATION = 'card_application',
  ADD_BANK_ACCOUNT = 'add_bank_account',
}

export enum PLAID_LINK_EVENT_STATUS {
  SUCCESS = 'success',
  FAILURE = 'failure',
}

export enum FRAUD_CONTROL_STATUS {
  active = 'active',
  inactive = 'inactive',
}

export enum SECURITY_CONTROL_STATUS {
  active = 'active',
  inactive = 'inactive',
}

export enum VEHICLE_SECURITY_CONTROL_STATUS {
  active = 'active',
  inactive = 'inactive',
}

export enum REPORTING_CONTROL_STATUS {
  active = 'active',
  inactive = 'inactive',
}

export enum METADATA_FIELD_STATUS {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export const FRAUD_CONTROL_CONFIRM_MODAL_TITLE = {
  enabled: $localize`Enable SMS Card Unlock?`,
  disabled: $localize`Disable SMS Card Unlock?`,
};

export const SECURITY_CONFIRM_MODAL_TITLE = {
  enabled: $localize`Enable Mobile-based unlock?`,
  disabled: $localize`Disable Mobile-Based Unlock?`,
};

export const REPORTING_CONFIRM_MODAL_TITLE = {
  enabled: $localize`Enable Vehicle ID? `,
  disabled: $localize`Disable Vehicle ID?`,
};

export const REPORTING_CARDHOLDER_TITLE = $localize`Cardholders without the latest Driver App`;
export const LOCKED_VEHICLE_CARD_TITLE = $localize`Lock card?`;

export const SPEND_PROFILE_KEBAB_MENU_ACTIONS = {
  EDIT: { // TODO: Remove this when remove fleetcard-granular-spend-control-enabled feature flag
    label: $localize`Edit`,
    key: 'edit',
  },
  EDIT_V2: {
    label: $localize`View spend profile`,
    key: 'edit',
  },
  DUPLICATE: {
    label: $localize`Duplicate`,
    key: 'duplicate',
    isDivider: true,
  },
  MAKE_DEFAULT: {
    label: $localize`Set as default`,
    key: 'make_default',
  },
  DELETE: {
    label: $localize`Delete`,
    key: 'delete',
    labelType: KEBAB_MENU_LABEL_TYPES.DANGER,
  },
  DIVIDER: {
    key: 'divider',
    label: '',
    isOptionDivider: true,
  },
};

export const MERCHANT_PROFILE_KEBAB_MENU_ACTIONS = {
  EDIT: {
    label: $localize`Edit`,
    key: 'edit',
  },
  DUPLICATE: {
    label: $localize`Duplicate`,
    key: 'duplicate',
    isDivider: true,
  },
  MAKE_DEFAULT: {
    label: $localize`Set as default`,
    key: 'make_default',
  },
  DELETE: {
    label: $localize`Delete`,
    key: 'delete',
    labelType: KEBAB_MENU_LABEL_TYPES.DANGER,
  },
  DIVIDER: {
    key: 'divider',
    label: '',
    isOptionDivider: true,
  },
};

export const SPEND_PROFILE_CARD_TOOLTIP_DISABLED_MESSAGES = {
  edit: $localize`You don’t have the permissions required to edit this profile. `,
  delete: $localize`You don’t have the permissions required to delete this profile. `,
  setAsDefault: $localize`You don’t have the permissions required to set this profile as default.`,
};

export type IMask = Array<string | RegExp> | ((raw: string) => Array<string | RegExp>) | false;

export const INDEPENDENT_TRUCK_STOPS_PARTNER_KEY: string = 'independent-truck-stops';

export const MOTIVE_FLEET_BUNDLE_KEY: string = 'motive-fleet-bundle';

export const NO_LIMITS_PROFILE_NAME: string = 'No limits';

export const CARD_ENTITIES = {
  VEHICLE: 'vehicle',
  DRIVER: 'driver',
  FLEET_USER: 'fleet_user',
  ASSET: 'asset',
};

export const CARD_ENTITIES_TITLES = {
  [CARD_ENTITIES.VEHICLE]: $localize`Vehicle`,
  [CARD_ENTITIES.DRIVER]: $localize`Driver`,
  [CARD_ENTITIES.FLEET_USER]: $localize`Fleet User`,
};

export const CARD_ENTITIES_PLURAL_TITLES = {
  [CARD_ENTITIES.DRIVER]: $localize`Drivers`,
  [CARD_ENTITIES.VEHICLE]: $localize`Vehicles`,
  [CARD_ENTITIES.FLEET_USER]: $localize`Fleet Users`,
};

export const ENTITY_TYPE = {
  DRIVER: 'driver',
  VEHICLE: 'vehicle',
  FLEET_USER: 'fleet_user',
};

export const ENTITY_TYPE_ICONS_PHX = {
  driver: Person,
  vehicle: VehicleTruck,
  fleet_user: PersonManager,
  asset: VehicleTrailer,
};

export enum REQUEST_CASH_ADVANCE_EVENTS {
  CARD_VIEWED = 'Fleet Card - Settings - Request Cash Advance widget viewed',
  REQUEST_BTN_CLICKED = 'Fleet Card - Settings - Request Cash Advance button clicked',
  REQUEST_SUBMITTED = 'Fleet Card - Settings - Cash Advance Request submitted',
}

// TODO: check if we can add more info in it like the property key for api call
export const CARD_SETTING_OVERRIDE_HEADINGS: IOverriddenSettings = {
  FUEL_LEVEL_ABOVE_THRESHOLD: {
    heading: $localize`Fuel level above threshold`,
    key: 'fuel_level_exceeds_limit',
    totalCards: 0,
  },
  VEHICLE_LOCATION_MISMATCH: {
    heading: $localize`Vehicle location does not match the merchant location`,
    key: 'vehicle_location_mismatch',
    totalCards: 0,
  },
  FUEL_TYPE_MISMATCH: {
    heading: $localize`Fuel type purchased does not match the fuel type of the vehicle`,
    key: 'fuel_type_mismatch',
    totalCards: 0,
  },
  FUEL_TANK_LEVEL_MISMATCH: {
    heading: $localize`Fuel purchased does not match the increase in tank level or fuel tank capacity`,
    key: 'fuel_tank_level_mismatch',
    totalCards: 0,
  },
  VEHICLE_PROXIMITY_MISMATCH: {
    heading: $localize`Vehicle location does not match the merchant location`,
    key: 'vehicle_proximity_mismatch',
    totalCards: 0,
  },
  MOBILE_BASED_UNLOCK_FOR_DRIVERS: {
    heading: $localize`Apply to cards assigned to drivers`,
    key: 'mobile_based_unlock_for_drivers',
    totalCards: 0,
  },
  MOBILE_BASED_UNLOCK_FOR_VEHICLES: {
    heading: $localize`Apply to cards assigned to vehicles`,
    key: 'mobile_based_unlock_for_vehicles',
    totalCards: 0,
  },
  MOBILE_BASED_UNLOCK_FOR_FLEET_USERS: {
    heading: $localize`Apply to cards assigned to fleet users`,
    key: 'mobile_based_unlock_for_fleet_users',
    totalCards: 0,
  },
};

export const CARDS_WITH_OVERRIDES_FILTER_DATA: IStrings = {
  key: 'cards-with-temporary-overrides',
  label: $localize`Cards with temporary overrides`,
};

export const SECURITY_SETTING_STATUS = {
  ENABLED: $localize`Enabled`,
  DISABLED: $localize`Disabled`,
};

export const MAX_TYPEAHEAD_ENTITY_SUGGESTIONS_COUNT: number = 8;

export const MY_CARD_TRACK_EVENTS = {
  OPEN_MY_CARD_PANEL: 'My Card - Open My Card panel',
};

export enum MY_CARD_OPEN_FROM {
  OVERVIEW_PAGE = 'overview_page',
  SIDEBAR = 'sidebar',
}

export const POLICIES_KEBAB_MENU_ACTIONS = {
  VIEW: {
    label: $localize`View policy`,
    key: 'VIEW',
  },
  SET_AS_DEFAULT: {
    label: $localize`Set as default`,
    key: 'SET_AS_DEFAULT',
  },
  DUPLICATE: {
    label: $localize`Duplicate`,
    key: 'DUPLICATE',
  },
  DELETE: {
    label: $localize`Delete`,
    key: 'DELETE',
  },
  DIVIDER: {
    key: 'DIVIDER',
    label: '',
  },
};

export const POLICIES_LIST_ACTIONS = {
  SHOW_CARDS_MODAL: 'SHOW_CARDS_MODAL',
};

export const POLICY_TYPES = {
  TRANSACTION_REQUIREMENTS: 'transaction_requirements',
  SPEND: 'spend',
  MERCHANT: 'merchant',
  SECURITY_AND_FRAUD: 'security_and_fraud',
};

export const POLICY_APP_ALERT_TYPES = {
  PRE_TXN_METADATA: 'pre_txn_metadata',
  POST_TXN_METADATA: 'post_txn_metadata',
};